import { Drawer, LoadError, StatusChip, SkeletonRows } from '~/components/ui';
import { formatLocalTime } from '~/lib/datetime';
import { formatDuration, formatMoney, formatNumber } from '~/lib/format';
import { useGetAnalyticsConversationQuery } from '~/modules/query/ai/analytics';
import { getAnalyticsErrorInfo, mapConversationInteractionStatus } from '~/lib/ai/analytics';

import { telemetryStatus } from '~/components/pages/ai/analytics/telemetryStatus';

interface ConversationDrawerProps {
  /** The row whose conversation is open; `null`/empty keeps the tray closed. */
  sessionId: string | null;
  onClose: () => void;
}

const Meta = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-800 dark:text-white/90">{value}</dd>
  </div>
);

/** The chat messages or interaction rows a telemetry session recorded, with its token and cost totals. */
export default function ConversationDrawer({ sessionId, onClose }: ConversationDrawerProps) {
  const { data, isLoading, isError, error, refetch } = useGetAnalyticsConversationQuery(
    sessionId ?? '',
    { skip: !sessionId },
  );
  const errorInfo = isError ? getAnalyticsErrorInfo(error) : null;

  return (
    <Drawer
      isOpen={!!sessionId}
      onClose={onClose}
      title="Conversation"
      width="lg"
      testId="ai-analytics-conversation"
    >
      {sessionId ? (
        isLoading ? (
          <SkeletonRows label="Loading conversation…" />
        ) : errorInfo || !data ? (
          <LoadError
            what="the conversation"
            message={errorInfo?.message}
            onRetry={() => refetch()}
            testId="ai-analytics-conversation"
          />
        ) : (
          <div className="space-y-6">
            <dl className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
              <Meta label="Messages" value={formatNumber(data.summary.messageCount)} />
              <Meta label="Total tokens" value={formatNumber(data.summary.totalTokens)} />
              <Meta label="Estimated cost" value={formatMoney(data.summary.estimatedCostUsd)} />
              <Meta label="Duration" value={formatDuration(data.summary.durationMs)} />
              <Meta label="Average latency" value={formatDuration(data.summary.avgLatencyMs)} />
              <Meta label="Started" value={formatLocalTime(data.summary.promptStartAt)} />
            </dl>

            <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
              Session {data.sessionId}
            </p>

            {'messages' in data ? (
              <div className="space-y-3">
                {data.messages.map((message) => (
                  <div
                    key={message.id}
                    className={message.role === 'user' ? 'text-right' : 'text-left'}
                  >
                    <span
                      className={`inline-block max-w-full rounded-lg px-3 py-2 text-left text-sm ${message.role === 'user' ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90'}`}
                    >
                      {message.content}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      {message.role === 'user' ? 'User' : 'Assistant'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-3">
                {data.interactions.map((interaction) => (
                  <li
                    key={interaction.id}
                    className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-gray-800"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {interaction.model}
                      </span>
                      <StatusChip
                        {...telemetryStatus(mapConversationInteractionStatus(interaction))}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatNumber(interaction.totalTokens)} tokens •{' '}
                      {formatMoney(interaction.estimatedCostUsd)} •{' '}
                      {formatDuration(interaction.durationMs)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatLocalTime(interaction.promptStartAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      ) : null}
    </Drawer>
  );
}
